import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@components/Button';
import { Input } from '@components/forms/Input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@components/Card';
import { AnonymousHeader } from '@components/layout/AnonymousHeader';
import { AnonymousFooter } from '@components/layout/AnonymousFooter';
import { authService } from './authService';

interface PasswordResetFormData {
  email: string;
}

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<PasswordResetFormData>({
    email: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const handleInputChange = (field: keyof PasswordResetFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess(false);

    try {
      await authService.resetPassword(formData.email);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while sending reset email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col password-reset-page">
        {/* Anonymous Header */}
        <AnonymousHeader />

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <Card className="w-full max-w-md shadow-2xl">
            <CardHeader className="text-center pb-8">
              <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <span className="material-icons text-2xl text-white">check</span>
              </div>
              <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
                {t('auth.checkYourEmail')}
              </CardTitle>
              <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
                {t('auth.emailSent')}
              </CardDescription>
            </CardHeader>

            <CardContent className="px-8 pb-8">
              <div className="text-center space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t('auth.accountExistsInstructions', { email: formData.email })}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  {t('auth.checkSpamFolder')}
                </p>
              </div>

              <div className="mt-8 space-y-4">
                <Button
                  variant="gradient-multi"
                  size="lg"
                  className="w-full"
                  onClick={handleBackToLogin}
                >
                  {t('auth.backToLogin')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Anonymous Footer */}
        <AnonymousFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-500 via-secondary-500 to-primary-600 flex flex-col password-reset-page">
      {/* Anonymous Header */}
      <AnonymousHeader />

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto mb-6 w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center">
              <span className="material-icons text-2xl text-white">lock_reset</span>
            </div>
            <CardTitle className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('auth.resetPasswordTitle')}
            </CardTitle>
            <CardDescription className="text-lg text-gray-600 dark:text-gray-400">
              {t('auth.resetPasswordSubtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Email Input */}
              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="block text-sm font-semibold text-gray-700 dark:text-gray-300"
                >
                  {t('auth.email')} *
                </label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  placeholder={t('auth.email')}
                  required
                  className="w-full"
                  disabled={isLoading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Reset Password Button */}
              <Button
                type="submit"
                variant="gradient-multi"
                size="lg"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('common.loading')}
                  </div>
                ) : (
                  t('auth.sendResetEmail')
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">
                  {t('common.or')}
                </span>
              </div>
            </div>

            {/* Back to Login Button */}
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              {t('auth.backToLogin')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Anonymous Footer */}
      <AnonymousFooter />
    </div>
  );
};

export { PasswordReset };
